// The Insights filter bar's options and the event it emits.
//
// PORTED from CGS2022 Scripts/InsightComponents/insightsHeaderExpanded.vue. The option values,
// the "All" handling and the shape of the emitted event (onInsightChange) are the same as
// CGS's, so each page's queryChanged(e) can be carried across unchanged. The pages already
// map this event onto the API's InsightsQuery. Keeping the event identical means that mapping
// is the only thing deciding what reaches the server, in both apps.

export const STATES = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI',
  'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
]

export const CHANNELS = ['Banked - Retail', 'Banked - Wholesale', 'Brokered', 'Correspondent']

export const DIVISIONS = [
  { display: 'Gold Star', value: 'DIGS01' },
  { display: 'Radiant', value: 'DIJJ01' },
  { display: 'Responsive', value: 'DIMB01' },
  { display: 'Ignite', value: 'DIIG01' },
  { display: 'City1st', value: 'DICF01' },
  { display: 'Principal', value: 'DIPR01' }
]

export const LIEN_POSITIONS = [
  { display: 'First Lien', value: 'FirstLien' },
  { display: 'Second Lien', value: 'SecondLien' }
]

export const AMORTIZATIONS = ['360', '300', '240', '180', '120'].map(v => ({ display: v, value: v }))

export const RATE_LOCKS = [
  { display: 'Locked', value: 'Locked' },
  { display: 'Unlocked', value: 'Unlocked' }
]

export const WAREHOUSE_LINES = [
  "Customer's Bank", 'JP Morgan Chase', 'M&T Bank', 'Flagstar Bank'
].map(v => ({ display: v, value: v }))

// Every filter the bar can show. `kind`:
//   'string' (the value is a plain string, as CGS sends branch, state and channel) or
//   'object' (the value is an {display, value} / LO object, which the page maps itself).
// `primary` filters sit in the bar. The rest go behind "More filters".
export const FILTER_DEFS = [
  { key: 'division', label: 'Division', kind: 'object', primary: true },
  { key: 'branch', label: 'Branch', kind: 'string', primary: true },
  { key: 'lo', label: 'Loan Officer', kind: 'object', primary: true },
  { key: 'channel', label: 'Channel', kind: 'string', primary: true },
  { key: 'bda', label: 'BDA', kind: 'object' },
  { key: 'state', label: 'State', kind: 'string' },
  { key: 'lien', label: 'Lien Position', kind: 'object' },
  { key: 'amortization', label: 'Amortization', kind: 'object' },
  { key: 'lock', label: 'Rate Lock', kind: 'object' },
  { key: 'warehouseLine', label: 'Warehouse Line', kind: 'object' }
]

// Which filters a page shows. This is the per-title v-if chain from
// insightsHeaderExpanded.vue, written out as a table:
//   - BDA is hidden on Loan Officer Analysis.
//   - Everything but Branch and Loan Officer is hidden on Dialpad Response Analysis.
//   - Amortization is hidden on Loan Risk Analysis.
//   - Rate Lock shows only on pages without a time frame (hideTimeFrame), and not on
//     Revenue Analysis - Active Locks or Loan Risk Analysis.
//   - Warehouse Line shows only on pages without a time frame, and not on Loan Officer
//     Analysis or Loan Risk Analysis.
// A page can still adjust its own set with `show` / `hide`.
export function visibleFilters({ title, timeFrameVisible, hide = [], show = [] }) {
  const isDialpad = title === 'Dialpad Response Analysis'
  const on = {
    division: !isDialpad,
    branch: true,
    lo: true,
    channel: !isDialpad,
    bda: !isDialpad && title !== 'Loan Officer Analysis',
    state: !isDialpad,
    lien: !isDialpad,
    amortization: !isDialpad && title !== 'Loan Risk Analysis',
    lock: !timeFrameVisible && !isDialpad && title !== 'Revenue Analysis - Active Locks' && title !== 'Loan Risk Analysis',
    warehouseLine: !timeFrameVisible && !isDialpad && title !== 'Loan Officer Analysis' && title !== 'Loan Risk Analysis'
  }
  show.forEach(k => { on[k] = true })
  hide.forEach(k => { on[k] = false })
  return FILTER_DEFS.filter(f => on[f.key])
}

// The selections, empty meaning "All". (CGS keeps an 'All' item in each MultiSelect and strips
// it in onInsightChange. Here an empty selection means All, and no 'All' item is ever stored.)
export function emptySelections() {
  return { division: [], branch: [], lo: [], channel: [], bda: [], state: [], lien: [], amortization: [], lock: [], warehouseLine: [] }
}

// onInsightChange(): the event every page's queryChanged(e) receives. Same keys, same value
// shapes, with "All" already stripped (an empty array), exactly as CGS sends it.
export function buildInsightChange(sel, window, azureId) {
  return {
    branch: sel.branch.slice(),
    lo: sel.lo.slice(),
    bda: sel.bda.slice(),
    lien: sel.lien.slice(),
    amortization: sel.amortization.slice(),
    lock: sel.lock.slice(),
    state: sel.state.slice(),
    channel: sel.channel.slice(),
    warehouseLine: sel.warehouseLine.slice(),
    insightStartDate: window.start,
    insightEndDate: window.end,
    insightTrendStartDate: window.trendStart,
    insightTrendEndDate: window.trendEnd,
    azureId,
    division: sel.division.slice()
  }
}

// The chips under the bar: one per active selection, labelled by its display text.
export function selectionChips(sel, defs) {
  const text = (key, v) => {
    if (typeof v === 'string') return v
    if (key === 'lo' || key === 'bda') return v.displayName || v.email || ''
    return v.display ?? v.value ?? ''
  }
  const chips = []
  defs.forEach(def => {
    (sel[def.key] || []).forEach((v, i) => chips.push({ key: def.key, index: i, label: def.label, text: text(def.key, v) }))
  })
  return chips
}

// CGS's corp-only warning: a window longer than two years is slow to query.
export function isLongWindow(start, end) {
  if (!start || !end) return false
  return Math.abs((end - start) / (365 * 24 * 60 * 60 * 1000)) > 2
}
